import socketAPI from "./socketAPI";
import store from "../store";
import { handleRemoteParticipantLeave } from "../pages/RoomPages/Session/SessionHelpers/Room/remoteParticipants";
/**
 * ==========================================================
 * ==================== INITIALIZATION ======================
 * ==========================================================
 */

/* ====== INITALIZE NAMESPACE SOCKET COMMUNICATES TO ====== */
const NAMESPACE = "/session";

/* ====== SOCKET STATES ====== */
let socketNSP = null; // Used for emitting socket events to the server

const sessionSocket = {
  /* ====== INITIALIZE SOCKET ====== */
  async initSessionSocket() {
    /** Custom listeners defined here because calendarSocket not available before initialization (hoisting) */
    const CUSTOM_LISTENERS = {
      connect: sessionSocket.joinSessionRooms,
      PARTNER_DISCONNECTED: sessionSocket.handlePartnerDisconnection,
      PARTNER_LEFT: sessionSocket.handlePartnerLeave,
      NEW_CHAT_MESSAGE: sessionSocket.handleNewMessage,
      PARTNER_STATE_CHANGED: sessionSocket.handlePartnerNewState,
      PARTNER_SUPPORT_STATE_CHANGED: sessionSocket.handlePartnerNewSupportState,
      PARTNER_REQUESTED_STATE: sessionSocket.handlePartnerRequestedState,
      PARTNER_STUCK_AT_LOADING: sessionSocket.handlePartnerStuckAtLoading,
      MATCH_CHANGED: sessionSocket.handleMatchChanged,
    };
    socketNSP = await socketAPI.initSocketNSP(NAMESPACE, CUSTOM_LISTENERS);

    /* ====== ENSURE PARTNER STATE IS ACCURATE AT START ====== */
    /* sessionSocket.requestPartnerState(); */ // Moved to room.js due to socket not being open on time here
  },

  /**
   * ==========================================================
   * ======================= RECEIVERS ========================
   * ==========================================================
   */

  /**
   * Handle disconnected partner
   */
  async handlePartnerDisconnection() {
    console.log("partner disconnected");
    /* This does not seem to actually catch internet connection failure (duh) so we resolve it via backend instead */
    /* await store.dispatch("sessionState/setPartnerState", "partnerDisconnected"); */
  },

  async handlePartnerLeave() {
    await handleRemoteParticipantLeave();
  },

  /**
   * Received new message
   * @param {Object} message
   * @returns
   */
  async handleNewMessage(message) {
    if (!message) return console.log("failed to receive message: ", message);
    message.received = true;
    await store.dispatch("session/addMessage", message);
  },

  /**
   * Received data contains these elements
   * @param {Number} data.dateTimeMS the datetime of the match in ms
   * @param {String} data.SID the session ID
   * @param {Object} data.partner the user with whom the user got matched.
   */
  async handleMatchChanged(data) {
    if (!data || !data.dateTimeMS) return console.log("data missing", data);
    /* Prevent changing between sessions */
    if (store.state.session.dateTimeMS == data.dateTimeMS) {
      await store.dispatch("session/changeMatch", data);
      /* Session is currently refreshed instead. */
      /* sessionSocket.joinChat(data.SID, data.dateTimeMS, !!data.partner); */
    }
  },

  /**
   ** Handle new received state from partner (brb etc)
   * @param {Object} data
   * @returns
   */
  async handlePartnerNewState(data) {
    if (!data || !data.state || !data.property || !data.sessionID || (!data.value && data.value !== false)) {
      return console.warn("state data missing");
    }
    await store.dispatch("mediastates/setPartnerState", data);
  },

  /**
   ** Handle new received support state from partner (coffee, tea, water, etc)
   * @param {Object} data
   * @returns
   */
  async handlePartnerNewSupportState(data) {
    if (!data || !data.sessionID || (!data.supportState && data.supportState !== "")) {
      return console.warn("state data missing");
    }
    await store.dispatch("mediastates/setPartnerSupportState", data);
  },

  /**
   * Handle a request from partner for user's state information
   * @param {String} sessionID
   * @returns
   */
  async handlePartnerRequestedState(sessionID) {
    if (!sessionID) return console.warn("SessionID missing, couldn't send state.");

    /* Gather state data */
    const ownState = store.state.mediastates.ownState;
    const stateData = {
      state: "brb",
      property: "value",
      value: ownState["brb"]["value"],
      supportState: ownState["brb"]["supportState"],
      sessionID: store.state.session.sessionID,
    };
    await sessionSocket.signalState(stateData);
  },

  async handlePartnerStuckAtLoading() {
    await store.dispatch("sessionState/setPartnerState", "partnerStuckAtLoading");
  },

  /**
   * ==========================================================
   * ======================== EMITTERS ========================
   * ==========================================================
   */
  async joinSessionRooms() {
    console.log("User connected to Session Socket");
    const sessionID = store.state.session.sessionID;
    const dateTimeMS = store.state.session.dateTimeMS;
    const isMatched = store.state.session.partnerMetadata;
    const UID = store.getters["auth/userID"];
    if (!UID || !sessionID || !dateTimeMS) return console.log("missing data for request");

    /* Prepare request data for sending */
    const sendData = { UID, sessionID, dateTimeMS, isMatched };

    return await socketNSP.emit("JOIN_SESSION_ROOMS", sendData);
  },

  async exitSessionRooms(SID, dateTimeMS) {
    const UID = store.getters["auth/userID"];
    if (!UID || !dateTimeMS) return console.log("Required data not provided for request.");

    /* Possibly never reaches past this because socket is already disconnected when this is called */
    if (!socketNSP.isConnected()) return console.log("socket already disconnected");

    /* Prepare request data for sending */
    const sendData = { UID, SID, dateTimeMS };

    await socketNSP.emit("EXIT_SESSION_ROOMS", sendData);
    socketNSP.removeEventListeners();
    socketNSP = null;
  },

  /* Send session partner your signal state */
  async signalState(stateData) {
    if (!stateData || !stateData.state || !stateData.property || (!stateData.value && stateData.value !== false) || !stateData.sessionID) {
      return console.warn("Cannot signal state, some state data is missing.");
    }

    return await socketNSP.emit("NEW_PARTNER_STATE", stateData);
  },

  /* Send session partner your supporting signal state */
  async signalSupportState(stateData) {
    if (!stateData || (!stateData.supportState && stateData.supportState !== "") || !stateData.sessionID) {
      return console.warn("Cannot signal support state, some state data is missing.");
    }

    return await socketNSP.emit("NEW_PARTNER_SUPPORT_STATE", stateData);
  },

  /* Pings partner to send a signal containing their current state status — used during initialization */
  async requestPartnerState() {
    const sessionID = store.state.session.sessionID;
    if (!sessionID) return console.warn("missing data for request");

    return await socketNSP.emit("REQUEST_PARTNER_STATE", sessionID);
  },

  async sendChatMessage(messageData) {
    /* Ensure there is any message or data to be sent */
    if (!messageData) return { success: true };

    /* If socket not connected, display reconnection message. */
    if (!socketNSP || !socketNSP.isConnected()) store.dispatch("session/setChatError", "Not connected to chat. Reconnecting...");

    /* Attempt send message (and reconnect if necessary) */
    const result = await socketNSP.emit("NEW_CHAT_MESSAGE", messageData);

    /* Handle result */
    if (result && result.success) store.dispatch("session/setChatError", "");
    else store.dispatch("session/setChatError", "Failed to connect to chat.");

    return result;
  },

  async emitUserStuckAtLoading() {
    const sessionID = store.state.session.sessionID;
    if (!sessionID) return console.warn("missing data for request");

    return await socketNSP.emit("USER_STUCK_AT_LOADING", sessionID);
  },
};

export default sessionSocket;
